import { useCallback, useEffect, useState } from "react";
import { getDueCounts, type DueCounts } from "../api/OutreachTouchesApi";

const REFRESH_MS = 10 * 60 * 1000;
export const DUE_REFRESH_EVENT = "hrms-due-touches-changed";

/** Tell every mounted counter (strip, dashboard card) to reload at once, e.g. after an action. */
export function announceDueChange() {
  window.dispatchEvent(new Event(DUE_REFRESH_EVENT));
}

/**
 * The global due counts behind the strip and the dashboard card. Loads on mount (and when `reloadKey` changes, e.g. the user
 * moved to another CRM page), when the tab becomes visible, every 10 minutes while visible (paused when hidden) and at
 * once after an action. A failed load keeps the last counts and never shows an error: the page itself is the source of truth.
 */
export function useDueCounts(enabled: boolean, reloadKey?: string) {
  const [counts, setCounts] = useState<DueCounts | null>(null);
  const load = useCallback(() => {
    getDueCounts().then(setCounts).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    load();
  }, [enabled, reloadKey, load]);

  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    const onChange = () => load();
    const timer = window.setInterval(() => { if (document.visibilityState === "visible") load(); }, REFRESH_MS);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener(DUE_REFRESH_EVENT, onChange);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener(DUE_REFRESH_EVENT, onChange);
    };
  }, [enabled, load]);

  return { counts, reload: load };
}
